import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import * as usersService from '../services/api/users';
import { transformUser } from '../services/transformers/userTransformer';
import { getUserId } from '../services/api/config';
import { checkUserAccess } from '../utils/userAccess';
import './ApprovalPending.css';

const ApprovalPending = () => {
  const navigate = useNavigate();
  const { user, logout } = useApp();
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const access = checkUserAccess(user);
    const userStatus = user.user_status?.toUpperCase();

    // If user type is not PICKER or ADMIN, redirect to access denied
    if (access.errorMessage) {
      navigate('/access-denied');
      return;
    }

    // If user status is ACTIVE or INACTIVE, redirect to dashboard
    if (userStatus === 'ACTIVE' || userStatus === 'INACTIVE') {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  const handleRefresh = async () => {
    const userId = getUserId();
    if (!userId || !user) return;

    try {
      setLoading(true);
      setStatusMessage('');
      
      const result = await usersService.getUserById(userId);
      
      if (result.success && result.data) {
        const updatedUser = transformUser(result.data);
        const userStatus = updatedUser.user_status?.toUpperCase();
        
        // If user status is ACTIVE or INACTIVE, redirect to dashboard
        if (userStatus === 'ACTIVE' || userStatus === 'INACTIVE') {
          setStatusMessage('Account verified! Redirecting...');
          setTimeout(() => navigate('/dashboard'), 1500);
          return;
        }
        
        // Check for access denied (wrong user type)
        const access = checkUserAccess(updatedUser);
        if (access.errorMessage) {
          navigate('/access-denied');
          return;
        }
        
        // Still NOT APPROVED
        setStatusMessage('Your application is still being reviewed.');
      } else {
        setStatusMessage('Unable to verify status. Please try again.');
      }
    } catch (error) {
      setStatusMessage('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  const access = checkUserAccess(user);
  const userStatus = user.user_status?.toUpperCase();
  
  // Only show this page if user status is NOT APPROVED
  if (access.errorMessage || userStatus === 'ACTIVE' || userStatus === 'INACTIVE') return null;

  return (
    <div className="approval-screen">
      <div className="approval-wrapper">
        {/* Header */}
        <header className="approval-header">
          <img src="/avro-logo-nobg.png" alt="Avro Recycling" className="header-logo" />
          <span className="header-brand">Avro Recycling</span>
        </header>

        {/* Main Content */}
        <main className="approval-content">
          {/* Status Visual */}
          <div className="status-visual">
            <div className="status-ring">
              <div className="status-ring-inner"></div>
            </div>
            <div className="status-icon-box">
              <svg viewBox="0 0 24 24" fill="none" className="status-icon">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
          </div>

          {/* Status Badge */}
          <div className="review-badge">
            <span className="badge-dot"></span>
            Under Review
          </div>

          {/* Main Message */}
          <h1 className="approval-title">Application Submitted</h1>
          <p className="approval-subtitle">
            Thank you for registering as a picker. Our team is reviewing your application and will notify you once approved.
          </p>

          {/* Timeline */}
          <div className="timeline-card">
            <div className="timeline-header">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Estimated Review Time</span>
            </div>
            <div className="timeline-value">24 – 48 Hours</div>
            <p className="timeline-note">Business days only. You'll receive a notification upon approval.</p>
          </div>

          {/* Check Status */}
          <button 
            className={`status-btn ${loading ? 'loading' : ''}`}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? (
              <span className="btn-loader"></span>
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {loading ? 'Verifying...' : 'Check Status'}
          </button>

          {/* Status Message */}
          {statusMessage && (
            <div className={`status-alert ${statusMessage.includes('approved') ? 'success' : ''}`}>
              {statusMessage}
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="approval-footer">
          <div className="footer-actions">
            <button onClick={handleLogout} className="footer-link">
              Sign Out
            </button>
            <span className="footer-divider">•</span>
            <a href="mailto:info@avrorecycling.com" className="footer-link">
              Contact Support
            </a>
          </div>
          <p className="footer-copyright">© 2026 Avro Recycling. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default ApprovalPending;
